import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import api from "../../services/api";
import { useAuth } from "../../contexts/AuthContext";
import "./Onboarding.css";

const defaultForm = {
  company_name: "",
  office_type: "",
  state: "",
  city: "",
  pin_code: "",
  address_line_1: "",
  address_line_2: "",
  pan: "",
  gstin: "",
  pfund_registration_no: "",
  esi_registration_no: "",
  prof_tax_registration_no: "",
  time_zone: Intl.DateTimeFormat().resolvedOptions().timeZone || "Asia/Calcutta",
  employee_id: "",
};

function CreateCompanyProfile() {
  const navigate = useNavigate();
  const { user, onboardingStage, onboardingPath, setOnboarding, refreshUser } = useAuth();
  const [formData, setFormData] = useState(defaultForm);
  const [stateOptions, setStateOptions] = useState([]);
  const [timeDisplay, setTimeDisplay] = useState("");
  const [concernPerson, setConcernPerson] = useState(null);
  const [loading, setLoading] = useState(false);
  const [initializing, setInitializing] = useState(true);

  useEffect(() => {
    if (onboardingStage && onboardingStage !== "create_company_profile") {
      navigate(onboardingPath || "/dashboard");
      return;
    }

    fetchMeta();
  }, [onboardingStage, onboardingPath, navigate]);

  const selectedState = useMemo(
    () => stateOptions.find((state) => state.key === formData.state || state.label === formData.state),
    [stateOptions, formData.state]
  );

  const cityOptions = selectedState?.cities || [];

  const fetchMeta = async () => {
    try {
      const response = await api.get("/onboarding/company-profile/meta");
      const payload = response.data?.data || {};
      const companyProfile = payload.company_profile || {};

      setStateOptions(payload.state_options || []);
      setTimeDisplay(payload.current_time_display || "");
      setConcernPerson(payload.concern_person || null);
      setFormData((prev) => ({
        ...prev,
        ...companyProfile,
        office_type: companyProfile.office_type || prev.office_type,
        time_zone: companyProfile.time_zone || prev.time_zone,
      }));
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Unable to load company profile",
        text: "Please refresh and try again.",
      });
    } finally {
      setInitializing(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
      ...(name === "state" ? { city: "" } : {}),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await api.post("/onboarding/company-profile", formData);
      const onboarding = response.data?.data?.onboarding;
      setOnboarding(onboarding);
      await refreshUser();

      Swal.fire({
        icon: "success",
        title: "Company profile saved",
        text: response.data?.message || "Company details have been saved.",
        timer: 1800,
        showConfirmButton: false,
      });

      navigate(onboarding?.path || "/select-company-plan");
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Save failed",
        text: error.response?.data?.message || "Unable to save company profile.",
      });
    } finally {
      setLoading(false);
    }
  };

  if (!user || onboardingStage !== "create_company_profile") {
    return null;
  }

  return (
    <div className="onboarding-shell compact-shell">
      <div className="onboarding-brandbar">
        <div className="onboarding-brand">
          <img
            src="https://cedurnow.com/assets/img/logos/new-logo.png"
            alt="Cedur"
            className="onboarding-brand-logo"
          />
          <div className="onboarding-brand-copy">
            <span className="onboarding-brand-kicker">Cedur Onboarding</span>
            <h1>Set up your company workspace</h1>
          </div>
        </div>
        <div className="onboarding-brand-note">
          <span>Step 3 of 4</span>
          <small>Your company profile helps us prepare payroll, compliance, and billing correctly.</small>
        </div>
      </div>

      <div className="onboarding-card company-profile-card">
        <div className="onboarding-body">
          <div className="section-title">Organization Details</div>

          {initializing ? (
            <p>Loading company profile form...</p>
          ) : (
            <form onSubmit={handleSubmit}>
              <div className="company-profile-panel">
                <div className="company-grid three-up">
                  <div className="onboarding-field">
                    <label>Company Name<span className="required">*</span></label>
                    <input name="company_name" value={formData.company_name} onChange={handleChange} />
                  </div>
                  <div className="onboarding-field">
                    <label>Phone Number<span className="required">*</span></label>
                    <input value={concernPerson?.phone_number || user?.phone_number || ""} readOnly />
                  </div>
                  <div className="onboarding-field">
                    <label>Email<span className="required">*</span></label>
                    <input value={concernPerson?.email || user?.email || ""} readOnly />
                  </div>
                </div>

                <div className="company-grid three-up">
                  <div className="onboarding-field">
                    <label>Office Type<span className="required">*</span></label>
                    <div className="radio-stack plain-radio-stack">
                      <label className="radio-inline">
                        <input
                          type="radio"
                          name="office_type"
                          value="Branch Office"
                          checked={formData.office_type === "Branch Office"}
                          onChange={handleChange}
                        />
                        <span>Branch Office</span>
                      </label>
                      <label className="radio-inline">
                        <input
                          type="radio"
                          name="office_type"
                          value="Reg. Office"
                          checked={formData.office_type === "Reg. Office"}
                          onChange={handleChange}
                        />
                        <span>Registered Office</span>
                      </label>
                    </div>
                  </div>
                  <div className="onboarding-field">
                    <label>State<span className="required">*</span></label>
                    <select name="state" value={formData.state} onChange={handleChange}>
                      <option value="">--- select state ---</option>
                      {stateOptions.map((state) => (
                        <option key={state.key} value={state.label}>{state.label}</option>
                      ))}
                    </select>
                  </div>
                  <div className="onboarding-field">
                    <label>City<span className="required">*</span></label>
                    <select name="city" value={formData.city} onChange={handleChange}>
                      <option value="">--- select city ---</option>
                      {cityOptions.map((city) => (
                        <option key={city} value={city}>{city}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="company-grid three-up">
                  <div className="onboarding-field">
                    <label>Pin Code<span className="required">*</span></label>
                    <input name="pin_code" value={formData.pin_code} onChange={handleChange} />
                  </div>
                  <div className="onboarding-field">
                    <label>Address Line 1<span className="required">*</span></label>
                    <input name="address_line_1" value={formData.address_line_1} onChange={handleChange} />
                  </div>
                  <div className="onboarding-field">
                    <label>Address Line 2</label>
                    <input name="address_line_2" value={formData.address_line_2} onChange={handleChange} />
                  </div>
                </div>

                <div className="company-grid four-up">
                  <div className="onboarding-field">
                    <label>PAN<span className="required">*</span></label>
                    <input name="pan" value={formData.pan} onChange={handleChange} style={{ textTransform: "uppercase" }} />
                  </div>
                  <div className="onboarding-field">
                    <label>GSTIN</label>
                    <input name="gstin" value={formData.gstin} onChange={handleChange} style={{ textTransform: "uppercase" }} />
                  </div>
                  <div className="onboarding-field">
                    <label>Providand Fund Registration No.</label>
                    <input
                      name="pfund_registration_no"
                      value={formData.pfund_registration_no}
                      onChange={handleChange}
                      style={{ textTransform: "uppercase" }}
                    />
                  </div>
                  <div className="onboarding-field">
                    <label>ESI Registration No.</label>
                    <input
                      name="esi_registration_no"
                      value={formData.esi_registration_no}
                      onChange={handleChange}
                      style={{ textTransform: "uppercase" }}
                    />
                  </div>
                </div>

                <div className="company-grid tax-row">
                  <div className="onboarding-field">
                    <label>Professional Tax Registration No.</label>
                    <input name="prof_tax_registration_no" value={formData.prof_tax_registration_no} onChange={handleChange} />
                  </div>
                  <div className="onboarding-field">
                    <label>Time Zone<span className="required">*</span></label>
                    <input value={timeDisplay || formData.time_zone} readOnly />
                  </div>
                </div>

                <div className="concern-heading">Concern Person</div>

                <div className="company-grid four-up">
                  <div className="onboarding-field">
                    <label>First Name<span className="required">*</span></label>
                    <input value={concernPerson?.first_name || user?.first_name || ""} readOnly />
                  </div>
                  <div className="onboarding-field">
                    <label>Last Name<span className="required">*</span></label>
                    <input value={concernPerson?.last_name || user?.last_name || ""} readOnly />
                  </div>
                  <div className="onboarding-field">
                    <label>Designation<span className="required">*</span></label>
                    <input value={concernPerson?.designation_name || ""} readOnly />
                  </div>
                  <div className="onboarding-field">
                    <label>Employee Id</label>
                    <input name="employee_id" value={formData.employee_id} onChange={handleChange} placeholder="Employee Id" />
                  </div>
                </div>

                <div className="company-form-footer">
                  <button type="submit" className="proceed-btn" disabled={loading}>
                    {loading ? "Saving..." : "Proceed"}
                  </button>
                </div>
              </div>
            </form>
          )}
        </div>
      </div>

      <div className="onboarding-footer">
        <span>Need help? Reach us at support@cedurnow.com</span>
        <span>© 2026 Cedur Enterprises Private Limited. All rights reserved.</span>
      </div>
    </div>
  );
}

export default CreateCompanyProfile;
